/**
 * School year utilities — mirrors backend chapters/school_years.py.
 *
 * School year runs Aug 1 – Jul 31.
 * Grade is derived from kStartYear, never stored.
 */

const KINDERGARTEN_CUTOFF_MONTH = 9; // Sept 1

// Grade ranges per stage
const ELEMENTARY_GRADES = [0, 1, 2, 3, 4, 5]; // K-5
const MIDDLE_GRADES = [6, 7, 8];
const HIGH_GRADES = [9, 10, 11, 12];

/** Aug 1 – Jul 31 boundary. Returns the start year of the school year. */
export function schoolYearOf(dt) {
  if (!dt) return null;
  const d = dt instanceof Date ? dt : new Date(dt);
  return d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1; // month is 0-indexed, 7 = Aug
}

export function currentSchoolYear() {
  return schoolYearOf(new Date());
}

/** US cutoff: born before Sept 1 → K at age 5, else age 6. */
export function defaultKStartYear(dob) {
  if (!dob) return null;
  const d = dob instanceof Date ? dob : new Date(dob);
  // month is 0-indexed in JS, cutoff month is 1-indexed (9 = September)
  if (d.getMonth() + 1 < KINDERGARTEN_CUTOFF_MONTH) {
    return d.getFullYear() + 5;
  }
  return d.getFullYear() + 6;
}

/** 0=K, 1-12=grades, -1=Pre-K, -2+=earlier, >12=after high school. */
export function gradeForYear(kStartYear, sy) {
  if (kStartYear == null) return null;
  return sy - kStartYear;
}

export function gradeLabel(grade) {
  if (grade == null) return '';
  if (grade < -2) return 'Baby & toddler';
  if (grade === -2) return 'Preschool';
  if (grade === -1) return 'Pre-K';
  if (grade === 0) return 'Kindergarten';
  const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' };
  const suffix = suffixes[grade] || 'th';
  if (grade <= 12) return `${grade}${suffix} grade`;
  return 'After high school';
}

export function gradeLabelShort(grade) {
  if (grade == null) return '';
  if (grade === 0) return 'K';
  if (grade <= 12) return String(grade);
  return gradeLabel(grade);
}

/** e.g. 2026 → '2026 – 27' */
export function schoolYearRange(sy) {
  return `${sy} – ${String(sy + 1).slice(-2)}`;
}

/** True if computed grade is between -1 (Pre-K) and 12. */
export function isSchoolAge(kStartYear, sy) {
  if (kStartYear == null) return false;
  const s = sy ?? currentSchoolYear();
  const grade = gradeForYear(kStartYear, s);
  return grade >= -1 && grade <= 12;
}

export function stageForGrade(grade) {
  if (grade == null) return 'Unknown';
  if (grade < -1) return 'Before school';
  if (ELEMENTARY_GRADES.includes(grade)) return 'Elementary';
  if (MIDDLE_GRADES.includes(grade)) return 'Middle School';
  if (HIGH_GRADES.includes(grade)) return 'High School';
  return 'After high school';
}

/** Compute kStartYear from a picked grade + current school year. */
export function kStartYearFromGrade(grade) {
  return currentSchoolYear() - grade;
}

/** Get pronoun from gender string. */
export function pronoun(gender) {
  if (gender === 'female') return { subject: 'She', possessive: 'Her' };
  if (gender === 'male') return { subject: 'He', possessive: 'His' };
  return { subject: 'They', possessive: 'Their' };
}
